import React, { useMemo } from "react";
import { Card, Typography, Box, useTheme } from "@mui/material";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
  Legend,
} from "recharts";
import { formatLocalDate } from "../../utils/formatDateTime";

export default function MessagesChart({ period, startDate, endDate, data }) {
  console.log("MessagesChart data:", data); // Debug log
  console.log("MessagesChart period:", period); // Debug log

  const theme = useTheme();

  const formattedData = useMemo(() => {
    if (!data || data.length === 0) return [];

    const merged = data.reduce((acc, curr) => {
      let key = "";
      let label = "";

      // Predefined periods
      if (period === "7 days") {
        key = curr.label;
        label = new Date(curr.label).toLocaleDateString("en-US", {
          weekday: "short",
        }); // "Mon", "Tue"
      } else if (period === "30 days") {
        key = curr.label;
        const startDate = new Date(data[0].label);
        const currentDate = new Date(curr.label);
        const dayCount =
          Math.ceil((currentDate - startDate) / (1000 * 60 * 60 * 24)) + 1;
        label = `Day ${dayCount}`;
      } else if (period === "12 months") {
        key = `${curr.year}-${curr.month}`;
        label = new Date(`${curr.year}-${curr.month}-01`).toLocaleDateString(
          "en-US",
          { month: "short" }
        ); // "Jan"
      }

      // Custom period
      // else if (period === "Custom") {
      //   if (curr.year && curr.month && !curr.label) {
      //     // Monthly custom
      //     key = `${curr.year}-${curr.month}`;
      //     label = new Date(`${curr.year}-${curr.month}-01`).toLocaleDateString("en-US", {
      //       month: "short",
      //       year: "numeric",
      //     }); // "Jan 2025"
      //   } else if (curr.year && !curr.month && !curr.label) {
      //     // Yearly custom
      //     key = `${curr.year}`;
      //     label = curr.year.toString(); // "2025"
      //   } else if (curr.label) {
      //     // Daily custom
      //     key = curr.label;
      //     label = new Date(curr.label).toLocaleDateString("en-US", {
      //       day: "numeric",
      //       month: "short",
      //     }); // "18 Sep"
      //   }
      // }
      else if (period === "Custom") {
        if (curr.year && curr.month && !curr.label) {
          // Monthly custom
          key = `${curr.year}-${curr.month}`;
          label = new Date(curr.year, curr.month - 1).toLocaleDateString(
            "en-US",
            {
              month: "short",
              year: "numeric",
            }
          ); // e.g., "Jan 2025"
        } else if (curr.year && !curr.month && !curr.label) {
          // Yearly custom
          key = `${curr.year}`;
          label = curr.year.toString();
        } else if (curr.label) {
          // Daily custom
          const parsedDate = new Date(curr.label);
          label = isNaN(parsedDate)
            ? curr.label
            : parsedDate.toLocaleDateString("en-US", {
                day: "numeric",
                month: "short",
                year: "numeric", // e.g., "18 Sep 2025"
              }); // e.g., "18 Sep"
          key = curr.label;
        }
      }

      // Fallback if key is still empty
      if (!key) key = curr.label || `${curr.year}-${curr.month || ""}`;

      if (!acc[key]) {
        acc[key] = { label, sent: 0, received: 0 };
      }

      if (curr.direction === "Outbound") acc[key].sent = curr.count;
      if (curr.direction === "Inbound") acc[key].received = curr.count;

      return acc;
    }, {});

    return Object.values(merged);
  }, [data, period]);

  return (
    <Card
      elevation={3}
      sx={{
        py: 2,
        borderRadius: 3,
        mb: 2,
        width: "100%",
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          ml: 5,
          mb: 2,
          flexWrap: "wrap",
        }}
      >
        <Typography variant="h6" fontWeight={700} color="text.primary">
          Messages
        </Typography>
        {period === "Custom" && startDate && endDate && (
          <Typography
            variant="body2"
            color="text.secondary"
            style={{ paddingRight: 32 }}
          >
            {formatLocalDate(startDate)} – {formatLocalDate(endDate)}
          </Typography>
        )}
      </Box>

      <ResponsiveContainer width="100%" height={300}>
        <LineChart
          data={formattedData}
          margin={{ top: 16, right: 32, left: 8, bottom: 8 }}
        >
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis
            dataKey="label"
            tick={{ fontSize: 12 }}
            interval={period === "7 days" ? 0 : period === "12 months" ? 0 : 1}
            padding={{ left: 16 }} // Add padding to X axis
          />
          <YAxis
            domain={[0, "auto"]}
            tickFormatter={(v) => {
              if (v >= 1000000) return `${(v / 1000000).toFixed(1)}M`;
              if (v >= 1000) return `${(v / 1000).toFixed(1)}k`;
              return v;
            }}
            tick={{ fontSize: 12 }}
            padding={{ bottom: 12 }} // Add padding to Y axis
          />

          <Tooltip
            formatter={(value, name) => [value.toLocaleString(), name]}
          />
          <Legend verticalAlign="top" align="right" iconType="circle" />
          <Line
            type="monotone"
            dataKey="sent"
            name="Sent"
            stroke={"#1565C0"}
            strokeWidth={2}
            strokeDasharray="5 5"
            dot={{ r: 4 }}
            activeDot={{ r: 6 }}
          />
          <Line
            type="monotone"
            dataKey="received"
            name="Received"
            stroke={"#F57C00"}
            strokeWidth={2}
            strokeDasharray="5 5"
            dot={{ r: 4 }}
            activeDot={{ r: 6 }}
          />
        </LineChart>
      </ResponsiveContainer>
    </Card>
  );
}
